import {
  Alert, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { executeShellCommandJson } from '../../api/shell';
import { sanitizeShellArg } from '../../utils/shell';

const ROLES = ['PLAYER', 'ADMIN', 'SUPER_ADMIN'] as const;

interface UserData {
  playerId: string;
  firebaseUid: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  createdAt: string | null;
  lastLoginAt: string | null;
}

interface UserListItem {
  playerId: string;
  displayName: string;
  roles: string[];
  createdAt: string | null;
  lastLoginAt: string | null;
}

interface RolesSetResult {
  ok: boolean;
  playerId: string;
  displayName: string;
  oldRoles: string[];
  newRoles: string[];
}

const roleColors: Record<string, 'error' | 'warning' | 'default'> = {
  SUPER_ADMIN: 'error',
  ADMIN: 'warning',
};

export function UserInspector() {
  const [identifier, setIdentifier] = useState('');
  const [user, setUser] = useState<UserData | null>(null);
  const [userList, setUserList] = useState<UserListItem[] | null>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [editResult, setEditResult] = useState<RolesSetResult | null>(null);

  async function handleLookup() {
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');
    setUser(null);
    setEditResult(null);
    try {
      const res = await executeShellCommandJson(`user.get ${sanitizeShellArg(identifier)}`);
      setUser(res as unknown as UserData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleList() {
    setLoading(true);
    setError('');
    setUserList(null);
    try {
      const roleArg = roleFilter ? ` --role ${sanitizeShellArg(roleFilter)}` : '';
      const res = await executeShellCommandJson(`user.list${roleArg}`);
      setUserList((res as unknown as { users: UserListItem[] }).users);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  function openRoleEditor() {
    if (!user) return;
    setEditRoles([...user.roles]);
    setEditResult(null);
    setEditOpen(true);
  }

  async function handleRolesSet() {
    if (!user || editRoles.length === 0) return;
    setLoading(true);
    setError('');
    setEditResult(null);
    try {
      const rolesArg = sanitizeShellArg(editRoles.join(','));
      const res = await executeShellCommandJson(
        `user.roles.set ${sanitizeShellArg(user.playerId)} ${rolesArg} --write`,
      );
      const result = res as unknown as RolesSetResult;
      setEditResult(result);
      setUser({ ...user, roles: result.newRoles });
      setEditOpen(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  function selectUserFromList(item: UserListItem) {
    setIdentifier(item.playerId);
    setUser(null);
    setUserList(null);
    setEditResult(null);
    setError('');
    setLoading(true);
    executeShellCommandJson(`user.get ${sanitizeShellArg(item.playerId)}`)
      .then((res) => setUser(res as unknown as UserData))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Request failed'))
      .finally(() => setLoading(false));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleLookup();
    }
  }

  return (
    <Stack spacing={3}>
      {/* Lookup */}
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">User Lookup</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Player ID or Firebase UID"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                placeholder="UUID or Firebase UID"
              />
              <Button variant="contained" onClick={handleLookup} disabled={loading}>
                Lookup
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* List */}
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">User List</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Filter by Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleList} disabled={loading}>
                List
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* User List Results */}
      {userList && (
        <Card sx={{ bgcolor: '#1a1d22' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Users ({userList.length})
            </Typography>
            {userList.length > 0 ? (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Display Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Player ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList.map((u) => (
                      <TableRow
                        key={u.playerId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => selectUserFromList(u)}
                      >
                        <TableCell>{u.displayName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {u.playerId}
                        </TableCell>
                        <TableCell>
                          {u.roles.map((r) => (
                            <Chip
                              key={r}
                              label={r}
                              size="small"
                              color={roleColors[r] ?? 'default'}
                              variant="outlined"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatTime(u.createdAt)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatTime(u.lastLoginAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No users found.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Detail */}
      {user && (
        <Card sx={{ bgcolor: '#1a1d22' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">User Profile</Typography>
              <Button variant="outlined" size="small" onClick={openRoleEditor}>
                Edit Roles
              </Button>
            </Stack>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Player ID</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{user.playerId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Firebase UID</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{user.firebaseUid}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Display Name</TableCell>
                  <TableCell>{user.displayName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                  <TableCell>
                    {user.roles.length > 0
                      ? user.roles.map((role) => (
                          <Chip key={role} label={role} size="small" color={roleColors[role] ?? 'default'} variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
                  <TableCell>
                    {user.permissions.length > 0
                      ? user.permissions.map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatTime(user.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Login</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatTime(user.lastLoginAt)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Role mutation feedback */}
      {editResult && (
        <Alert severity="success">
          Roles updated for {editResult.displayName}: {editResult.oldRoles.join(', ')} → {editResult.newRoles.join(', ')}
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {/* Role Editor Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Roles — {user?.displayName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            {ROLES.map((role) => (
              <Button
                key={role}
                variant={editRoles.includes(role) ? 'contained' : 'outlined'}
                color={roleColors[role] === 'error' ? 'error' : roleColors[role] === 'warning' ? 'warning' : 'primary'}
                onClick={() =>
                  setEditRoles((prev) =>
                    prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
                  )
                }
              >
                {role}
              </Button>
            ))}
            {editRoles.length === 0 && (
              <Typography variant="body2" color="error">At least one role must be selected</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRolesSet}
            disabled={loading || editRoles.length === 0}
          >
            Save Roles
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
